import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import getConfig from 'next/config';

import Layout from '../../../components/LightLP/Layout';
import LogoWall from '../../../components/LightLP/LogoWall';
import Tabs from '../../../components/LightLP/Tabs';
import StoryWithSignup from '../../../components/LightLP/StoryWithSignup';
import Faq from '../../../components/LightLP/Faq';
import JobSlider from '../../../components/LightLP/JobSlider';
import World from '../../../components/LightLP/World';
import HeroWithOnboarding from '../../../components/LightLP/HeroWithOnboarding';
import FooterWithOnboarding from '../../../components/LightLP/FooterWithOnboarding';
import onboardingRoles from '../../../components/LightLP/OnboardingRolesArray';
import onboardingYears from '../../../components/LightLP/OnboardingYearsOfExperience';
import sitePath from '../../../helpers/sitePath';
import {
  lightTheme,
  tabs,
  successStory,
  skillsOptions,
  jobs,
  faq,
} from '../../../helpers/engineers-apply-config';

const { publicRuntimeConfig } = getConfig();
const signUpUrl = publicRuntimeConfig.signUpUrl;
const onboardingUrl = publicRuntimeConfig.onboardingUrl;

import sharecardImage from '../../../public/images/sharecard.jpg';

import map from '../../../public/images/light-lp/poland/map.svg';
import avatar1 from '../../../public/images/light-lp/poland/avatar-1.png';
import avatar2 from '../../../public/images/light-lp/poland/avatar-2.png';
import avatar3 from '../../../public/images/light-lp/poland/avatar-3.png';
import avatar4 from '../../../public/images/light-lp/poland/avatar-4.png';
import avatar5 from '../../../public/images/light-lp/poland/avatar-5.png';
import avatar6 from '../../../public/images/light-lp/poland/avatar-6.png';
import avatar7 from '../../../public/images/light-lp/poland/avatar-7.png';
import avatar8 from '../../../public/images/light-lp/poland/avatar-8.png';
import metaRobots from '@helpers/metaRobots';

const icons = [
  {
    url: avatar1,
    position: { top: '25%', left: '27%' },
  },
  {
    url: avatar2,
    position: { top: '35%', left: '34%' },
  },
  {
    url: avatar3,
    position: { top: '60%', left: '36%' },
  },
  {
    url: avatar4,
    position: { top: '67%', left: '43%' },
  },
  {
    url: avatar5,
    position: { top: '58%', left: '51%' },
  },
  {
    url: avatar6,
    position: { top: '80%', left: '53%' },
  },
  {
    url: avatar7,
    position: { top: '35%', left: '54%' },
  },
  {
    url: avatar8,
    position: { top: '67%', left: '62%' },
  },
];

const meta = {
  title: 'Unlock Exceptional Engineering Opportunities in Poland | Terminal',
  description: '',
};

const logoWall = {
  title: 'Talent nie ogranicza się do geografii',
  text: 'That is why we source talented developers - YOU - from all around the world. With Terminal, you have the ability to work remotely - spend your weekends exploring UNESCO World Heritage sites like Malbork Castle or go back in time with a meal at Piwnica Swidnicka in Wrocław. No matter where you live, be it Kraków, Warsaw, Tricity, Poznań or anywhere else, you can find stable, high-paying opportunities with Terminal.',
};

const world = {
  title: 'Terminal Across Poland',
  text: 'Grow your career and your network starting today',
  country: '',
  stats: [
    {
      value: '#3',
      text: 'Country with Best Developers (Hackernet)',
      bgColor: '#B8E5CF',
    },
    {
      value: '#14/20',
      text: 'Top Digital Nation',
      bgColor: '#CBD3E1',
    },
    {
      value: '#20/100',
      text: 'Krakow Super City (Global Innovation Index)',
      bgColor: '#7CCEE0',
    },
  ],
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function ApplyPoland() {
  const router = useRouter();
  const [utmParams, setUtmParams] = useState<string>('');

  useEffect(() => {
    window.location.search && setUtmParams(window.location.search);
  }, []);

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="ogdescription" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout
        theme={lightTheme}
        navItems={[
          {
            label: 'About Terminal',
            href: '#about',
          },
          {
            label: 'Success Stories',
            href: '#success-stories',
          },
          {
            label: 'Available Jobs',
            href: '#available-jobs',
          },
          {
            label: 'FAQs',
            href: '#faqs',
          },
          {
            label: 'Join Terminal',
            href: signUpUrl + utmParams,
            type: 'button',
          },
        ]}
        headerButton={{
          url: signUpUrl + utmParams,
          text: 'Join Terminal',
        }}
      >
        <HeroWithOnboarding
          hero={{
            title: {
              main: 'Earn. Learn.',
              highlight: 'Stay home.',
            },
            text: 'Terminal candidates who complete their profiles receive 2-3 interviews within 2 weeks.',
            italic: 'Are you going to let another day pass you by?',
            button: {
              text: 'Join Terminal',
              url: signUpUrl + utmParams,
            },
          }}
          theme={lightTheme}
          onboardingRoles={onboardingRoles}
          signUpUrl={onboardingUrl}
        />
        <LogoWall title={logoWall.title} text={logoWall.text} />
        <Tabs tabs={tabs('30k-54k PLN/month')} />
        <StoryWithSignup
          story={successStory}
          skillsOptions={skillsOptions}
          theme={lightTheme}
          variant="1"
        />
        <JobSlider jobs={jobs('30k-54k PLN/month')} />
        <World world={world} icons={icons} map={map} />
        <FooterWithOnboarding
          onboardingRoles={onboardingRoles}
          onboardingYears={onboardingYears}
          signUpUrl={onboardingUrl}
          theme={lightTheme}
        />
        <Faq faq={faq} />
      </Layout>
    </>
  );
}
